import { ReactComponent as SortLogo } from '@/assets/sort_descend.svg'
import CreateCourseProgramDialog from '@/components/CreateCourseProgramDialog'
import EditCourseProgramDialog from '@/components/EditCourseProgramDialog'
import EditCourseProgramsDialog from '@/components/EditCourseProgramsDialog'
import EditProgramCourseDialog from '@/components/EditProgramCourseDialog'
import PreviewCourseVideoDialog from '@/components/PreviewCourseVideoDialog'
import VideoPreviewDialog from '@/components/VideoPreviewDialog'
import usePermissions from '@/hooks/usePermissions'
import Button from '@/shared/Button'
import ConfirmDialog from '@/shared/ConfirmDialog'
import FormCreator from '@/shared/FormCreator'
import AddCircle from '@mui/icons-material/AddCircle'
import Box from '@mui/material/Box'
import Container from '@mui/material/Container'
import Grid from '@mui/material/Grid'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import get from 'lodash/get'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import ProgramItem from './ProgramItem'

const LIST_PAGE_SIZE = 10

const mapStateToProps = ({ loading, courseProgram }) => ({
  courseProgramList: courseProgram.courseProgramList,
  selectedProgram: courseProgram.selectedProgram,
  isLoading: loading['courseProgram/getCourseProgram'],
  deleteLoading: loading['courseProgram/deleteCourseProgram'],
  getProgramLoading: loading['courseProgram/getCourseProgramById'],
})

const MyVideos = ({
  dispatch,
  courseProgramList,
  selectedProgram,
  isLoading,
  deleteLoading,
  getProgramLoading,
}) => {
  const { t } = useTranslation()
  const form = useRef()
  const rootContainerRef = useRef()
  const pagingRef = useRef()
  const loadingRef = useRef()
  const pageRef = useRef()

  const [page, setPage] = useState(0)
  const [createCourseProgramOpen, setCreateCourseProgramOpen] = useState(false)
  const [deleteProgramData, setDeleteProgramData] = useState()
  const [removeCourseData, setRemoveCourseData] = useState()
  const [currentOpen, setCurrentOpen] = useState()
  const [editProgramData, setEditProgramData] = useState()
  const [editProgramCourseData, setEditProgramCourseData] = useState()
  const [editCourseProgramsData, setEditCourseProgramsData] = useState()
  const [previewCourseData, setPreviewCourseData] = useState()
  const [videoPreviewData, setVideoPreviewData] = useState()
  const [sortAsc, setSortAsc] = useState(false)

  const { data, paging = {} } = courseProgramList || {}

  const canCreate = usePermissions('courseVideosList_create')

  const fetchData = useCallback(() => {
    const formValues = form.current?.getValues()
    const { search } = formValues || {}

    const payload = {
      searchParam: search,
      pageIndex: page,
      pageSize: LIST_PAGE_SIZE,
      sort: sortAsc ? 'COURSE_PROGRAM_NAME_ASC' : 'COURSE_PROGRAM_NAME_DESC',
    }

    dispatch({
      type: 'courseProgram/getCourseProgram',
      payload,
      infinite: page > 0,
    })
  }, [dispatch, page, sortAsc, form])

  const deleteProgram = () => {
    dispatch({
      type: 'courseProgram/deleteCourseProgram',
      payload: { programId: deleteProgramData.programId },
      successCallback: () => {
        dispatch({
          type: 'app/notify',
          payload: {
            message: t('messages.program_deleted', {
              name: deleteProgramData.name,
            }),
            severity: 'success',
          },
        })

        setDeleteProgramData()
      },
    })
  }

  const deleteProgramCourse = () => {
    dispatch({
      type: 'courseProgram/deleteCourseProgramCourse',
      payload: {
        programId: get(removeCourseData, 'program.programId'),
        courseTempId: get(removeCourseData, 'course.templateId'),
      },
      successCallback: () => {
        dispatch({
          type: 'app/notify',
          payload: {
            message: t('messages.program_course_removed', {
              program: get(removeCourseData, 'program.name'),
              course: get(removeCourseData, 'course.name'),
            }),
            severity: 'success',
          },
        })

        dispatch({
          type: 'courseProgram/getCourseProgramById',
          payload: { programId: get(removeCourseData, 'program.programId') },
          successCallback: responseData => {
            dispatch({ type: 'courseProgram/setSelectedProgram', payload: responseData })
          },
        })

        setRemoveCourseData()
      },
    })
  }

  useEffect(() => {
    fetchData()
  }, [fetchData])

  useEffect(() => {
    setPage(0)
  }, [sortAsc])

  useEffect(() => {
    pagingRef.current = paging
    loadingRef.current = isLoading
    pageRef.current = page
  }, [paging, isLoading, page])

  useEffect(() => {
    const onScroll = e => {
      const scrollNode = e.currentTarget

      if (scrollNode.scrollTop + scrollNode.clientHeight >= scrollNode.scrollHeight - 10) {
        const { hasNext } = pagingRef.current || {}

        if (!loadingRef.current && hasNext) {
          setPage(pageRef.current + 1)
        }
      }
    }

    rootContainerRef.current?.parentElement.addEventListener('scroll', onScroll, false)

    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      rootContainerRef.current?.parentElement?.removeEventListener('scroll', onScroll, false)
    }
  }, [])

  const isEmptyData = data?.length === 0 && !isLoading

  return (
    <Container ref={rootContainerRef} maxWidth={false} sx={{ flex: 1, display: 'flex' }}>
      <Grid container gap={4} flexDirection="column">
        <Grid item>
          <Grid container>
            <Grid item>
              <FormCreator
                ref={form}
                formItems={[
                  {
                    component: () => (
                      <Button
                        variant="text"
                        color="secondary_80"
                        endIcon={
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              transform: `rotateX(${sortAsc ? '180' : '0'}deg)`,
                            }}
                          >
                            <SortLogo
                              sx={{
                                color: '#2D2D6C80',
                              }}
                            />
                          </Box>
                        }
                        onClick={() => {
                          setSortAsc(!sortAsc)
                        }}
                      >
                        {t('general.sort_list')}
                      </Button>
                    ),
                    name: 'sort_list',
                  },
                  ...(canCreate
                    ? [
                        {
                          component: () => (
                            <Button
                              variant="text"
                              color="secondary_80"
                              endIcon={<AddCircle />}
                              onClick={() => {
                                setCreateCourseProgramOpen(true)
                              }}
                            >
                              {t('general.add_training_program_list')}
                            </Button>
                          ),
                          name: 'add_program',
                        },
                      ]
                    : []),
                  {
                    name: 'search',
                    defaultValue: '',
                    placeholder: t('general.insert_keyword'),
                    inputConfig: {
                      onSubmit: async () => {
                        setPage(0)
                        fetchData()
                      },
                    },
                  },
                ]}
                flexDirection="row"
                submitComponent={() => null}
                resetComponent={() => null}
              />
            </Grid>
          </Grid>
        </Grid>

        <Grid item>
          {!isEmptyData && (
            <Stack>
              {data?.map((program, index) => {
                const open = program.programId === currentOpen

                return (
                  <ProgramItem
                    open={open}
                    onReorder={({ programId, courseTempId, from, to }) => {
                      dispatch({
                        type: 'courseProgram/editProgramCourseOrder',
                        payload: { programId, courseTempId, from, to },
                      })
                    }}
                    loading={getProgramLoading}
                    selectedDetails={selectedProgram}
                    key={`program_${index + 1}`}
                    {...program}
                    getDetails={() => {
                      if (open) {
                        setCurrentOpen()
                      } else {
                        setCurrentOpen(program.programId)
                      }

                      dispatch({
                        type: 'courseProgram/getCourseProgramById',
                        payload: { programId: program.programId },
                        successCallback: responseData => {
                          dispatch({
                            type: 'courseProgram/setSelectedProgram',
                            payload: responseData,
                          })
                        },
                      })
                    }}
                    onDelete={() => {
                      setDeleteProgramData(program)
                    }}
                    onEditCourse={() => {
                      setEditProgramCourseData(program)
                    }}
                    onEdit={() => {
                      setEditProgramData(program)
                    }}
                    onEditCoursePrograms={courseData => {
                      setEditCourseProgramsData(courseData)
                    }}
                    onCoursePreview={courseData => {
                      setPreviewCourseData(courseData)
                    }}
                    onVideoPreview={courseData => {
                      setVideoPreviewData(courseData)
                    }}
                    onDeleteCourse={setRemoveCourseData}
                  />
                )
              })}
            </Stack>
          )}

          {isEmptyData && (
            <Box
              sx={{
                display: 'flex',
                height: '100%',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography variant="h6">{t('errors.no_course')}</Typography>
            </Box>
          )}
        </Grid>
      </Grid>

      <CreateCourseProgramDialog
        open={createCourseProgramOpen}
        onClose={() => {
          setCreateCourseProgramOpen(false)
        }}
        onComplete={() => {
          setPage(0)
          fetchData()
        }}
      />

      <EditCourseProgramDialog
        data={editProgramData}
        open={!!editProgramData}
        onClose={() => {
          setEditProgramData()
        }}
      />

      <ConfirmDialog
        open={!!deleteProgramData}
        title={t('messages.confirm_delete')}
        onClose={() => {
          setDeleteProgramData()
        }}
        onSubmit={() => {
          deleteProgram()
        }}
        loading={deleteLoading}
      >
        <Stack alignItems="center">
          <Typography>
            {t('messages.confirm_delete_program', {
              name: deleteProgramData?.name,
            })}
          </Typography>
        </Stack>
      </ConfirmDialog>

      <ConfirmDialog
        open={!!removeCourseData}
        title={t('messages.confirm_delete')}
        onClose={() => {
          setRemoveCourseData()
        }}
        onSubmit={() => {
          deleteProgramCourse()
        }}
        loading={deleteLoading}
      >
        <Stack alignItems="center">
          <Typography>
            {t('messages.confirm_remove_program_course', {
              name: deleteProgramData?.name,
              program: get(removeCourseData, 'program.name', ''),
              course: get(removeCourseData, 'course.name', ''),
            })}
          </Typography>
        </Stack>
      </ConfirmDialog>

      {editProgramCourseData && (
        <EditProgramCourseDialog
          open={!!editProgramCourseData}
          programData={editProgramCourseData}
          onClose={() => {
            setEditProgramCourseData()
          }}
        />
      )}

      {editCourseProgramsData && (
        <EditCourseProgramsDialog
          open={!!editCourseProgramsData}
          courseData={editCourseProgramsData}
          onComplete={() => {
            dispatch({
              type: 'courseProgram/getCourseProgramById',
              payload: { programId: selectedProgram.programId },
              successCallback: responseData => {
                dispatch({
                  type: 'courseProgram/setSelectedProgram',
                  payload: responseData,
                })
              },
            })
          }}
          onClose={() => {
            setEditCourseProgramsData()
          }}
        />
      )}

      {previewCourseData && (
        <PreviewCourseVideoDialog
          editable={false}
          submitText={t('general.close')}
          courseData={previewCourseData}
          onClose={() => {
            setPreviewCourseData()
          }}
        />
      )}

      {videoPreviewData && (
        <VideoPreviewDialog
          courseData={videoPreviewData}
          onClose={() => {
            setVideoPreviewData()
          }}
        />
      )}
    </Container>
  )
}

export default connect(mapStateToProps)(MyVideos)
